// Mixing wie in Pro Tools: Aux-Busse und Routing, Mix- und Edit-Gruppen, Freeze-Signatur.
// Reine Funktionen auf dem Projekt (innerhalb von commit()).
import type { Group, Project, Track } from '@srs/shared';
import { uid } from '../state/demo.ts';
import { bussesOf, defaultBusses } from '../audio/plugins.ts';

export const GROUP_COLORS = ['#003399', '#c7861b', '#0f8b8d', '#6b4fbb', '#d0543f', '#1f8a5a', '#b5537a', '#5a6478'];

// ---- Aux-Busse und Routing ----
/** Neuen Aux-Bus anlegen (ohne Plugins) */
export function addBus(p: Project, name?: string): string {
  p.busses ??= defaultBusses();
  const id = uid('bus');
  p.busses.push({ id, name: name ?? `Aux ${p.busses.length + 1}`, color: GROUP_COLORS[p.busses.length % GROUP_COLORS.length], kind: 'bus', armed: false, mute: false, solo: false, volumeDb: 0, pan: 0, sends: [], inserts: [] });
  return id;
}
/** Bus löschen: Sends dorthin entfallen, Spuren mit diesem Ausgang gehen wieder auf den Master */
export function deleteBus(p: Project, busId: string) {
  p.busses = bussesOf(p).filter((b) => b.id !== busId);
  for (const t of p.tracks) {
    t.sends = t.sends.filter((s) => s.busId !== busId);
    if (t.output === busId) t.output = undefined;
    if (t.automation?.lanes) delete t.automation.lanes[`send:${busId}`];
  }
}
/** Ausgang von Spuren setzen (Bus-Id oder undefined = Master) */
export function routeTracks(p: Project, trackIds: string[], busId: string | undefined) {
  const ok = busId === undefined || bussesOf(p).some((b) => b.id === busId);
  for (const t of p.tracks) if (trackIds.includes(t.id) && t.kind !== 'bus') t.output = ok ? busId : undefined;
}
/** Ausgang einer Spur als Name */
export const outputName = (p: Project, t: Track) => (t.output ? bussesOf(p).find((b) => b.id === t.output)?.name ?? 'Master' : 'Master');

// ---- Gruppen ----
export const groupsOf = (p: Project): Group[] => p.groups ?? [];
/** Neue Gruppe aus Spuren; Name nach Inhalt geraten */
export function makeGroup(p: Project, trackIds: string[], name?: string): string | null {
  const ids = p.tracks.filter((t) => trackIds.includes(t.id) && t.kind !== 'bus').map((t) => t.id);
  if (ids.length < 1) return null;
  const names = p.tracks.filter((t) => ids.includes(t.id)).map((t) => t.name);
  const guess = name ?? (names.every((n) => /vox|vocal|gesang|chor|harm|bv/i.test(n)) ? 'Vocals' : names.every((n) => /kick|snare|hat|drum|perc|tom|clap|oh|room/i.test(n)) ? 'Drums' : names.every((n) => /git|guit/i.test(n)) ? 'Gitarren' : `Gruppe ${groupsOf(p).length + 1}`);
  const id = uid('grp');
  (p.groups ??= []).push({ id, name: guess, color: GROUP_COLORS[groupsOf(p).length % GROUP_COLORS.length], trackIds: ids, mix: true, edit: true, active: true });
  return id;
}
export function deleteGroup(p: Project, id: string) { p.groups = groupsOf(p).filter((g) => g.id !== id); }
/** Alle Spuren, die mit trackId über aktive Gruppen der Art verbunden sind (inkl. trackId) */
export function groupMembers(p: Project, trackId: string, kind: 'mix' | 'edit'): string[] {
  const out = new Set([trackId]);
  for (const g of groupsOf(p)) if (g.active && g[kind] && g.trackIds.includes(trackId)) g.trackIds.forEach((id) => out.add(id));
  return [...out].filter((id) => p.tracks.some((t) => t.id === id));
}
/** Auswahl über Edit-Gruppen erweitern (Reihenfolge wie die Spuren) */
export function expandEdit(p: Project, trackIds: string[]): string[] {
  const set = new Set<string>();
  for (const id of trackIds) for (const m of id.includes(':') || !p.tracks.some((t) => t.id === id) ? [id] : groupMembers(p, id, 'edit')) set.add(m);
  const order = p.tracks.map((t) => t.id);
  if (!groupsOf(p).some((g) => g.active && g.edit)) return trackIds;   // ohne Edit-Gruppen nichts verändern
  return [...set].sort((a, b) => (order.indexOf(a.split(':')[0]) - order.indexOf(b.split(':')[0])) || a.localeCompare(b));
}
/** Clips auf Spuren derselben Edit-Gruppe, die zeitlich zu den gewählten passen (für Klick und Verschieben) */
export function editGroupClips(p: Project, ids: string[]): string[] {
  const out = new Set(ids);
  for (const id of ids) {
    const c = p.clips.find((x) => x.id === id);
    if (!c) continue;
    const members = groupMembers(p, c.trackId, 'edit').filter((t) => t !== c.trackId);
    for (const o of p.clips) {
      if (!members.includes(o.trackId)) continue;
      const ov = Math.min(c.start + c.duration, o.start + o.duration) - Math.max(c.start, o.start);
      if (ov > 0.5 * Math.min(c.duration, o.duration)) out.add(o.id);
    }
  }
  return [...out];
}
/** Fader in der Mix-Gruppe: alle Mitglieder um dieselbe Differenz in dB (wie Pro Tools, relativ) */
export function groupVolume(p: Project, trackId: string, db: number) {
  const t = p.tracks.find((x) => x.id === trackId);
  if (!t) return;
  const d = db - t.volumeDb;
  for (const id of groupMembers(p, trackId, 'mix')) { const m = p.tracks.find((x) => x.id === id)!; m.volumeDb = id === trackId ? db : Math.max(-60, Math.min(12, m.volumeDb + d)); }
}
/** Mute oder Solo in der Mix-Gruppe: alle bekommen den neuen Zustand der angeklickten Spur */
export function groupToggle(p: Project, trackId: string, what: 'mute' | 'solo') {
  const t = p.tracks.find((x) => x.id === trackId);
  if (!t) return;
  const v = !t[what];
  for (const id of groupMembers(p, trackId, 'mix')) { const m = p.tracks.find((x) => x.id === id); if (m) m[what] = v; }
}
/** Gruppen-Ids einer Spur (für die Anzeige am Spurkopf) */
export const groupsOfTrack = (p: Project, trackId: string) => groupsOf(p).filter((g) => g.trackIds.includes(trackId));

// ---- Freeze ----
const fnv = (s: string) => { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return (h >>> 0).toString(36); };
/** Stand einer Spur, der den gerechneten Klang bestimmt (Clips, Inserts samt Plugin-Automation, Instrument, Tempo) */
const sigCache = new WeakMap<Project, Map<string, string>>();
export function freezeSig(p: Project, t: Track): string {
  let m = sigCache.get(p);
  if (!m) { m = new Map(); sigCache.set(p, m); }
  const have = m.get(t.id);
  if (have !== undefined) return have;
  const sig = computeSig(p, t);
  m.set(t.id, sig);
  return sig;
}
function computeSig(p: Project, t: Track): string {
  const clips = p.clips.filter((c) => c.trackId === t.id).map((c) => ({ ...c, id: undefined, by: undefined, name: undefined, groupId: undefined }));
  // Panorama wird mit eingefroren (Freeze rechnet nach dem Panner, vor dem Fader)
  const auto = Object.fromEntries(Object.entries(t.automation?.lanes ?? {}).filter(([k]) => k.startsWith('ins:') || k === 'pan'));
  return fnv(JSON.stringify({ clips, ins: t.inserts, auto, pan: t.pan, inst: t.instrument, el: t.elastic, tempo: [p.bpm, p.tempos, p.meters, p.beatsPerBar] }));
}
/** Spielt die Spur gerade ihre eingefrorene Fassung? (Nur solange sich nichts geändert hat) */
export const isFrozen = (p: Project, t: Track) => !!t.frozen && t.frozen.sig === freezeSig(p, t);
